/**
 * Generador de plantillas Excel para edición masiva con columnas bloqueadas
 * Usa ExcelJS para protección de celdas (llaves, IDs)
 */

import ExcelJS from 'exceljs';

/**
 * Crea una plantilla Excel para editar productos
 * Columnas bloqueadas: id_product
 * Columnas editables: nombre, descripcion, categoria
 */
export const generateBulkEditProductsExcel = async (products, categories) => {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'DotoniWoof';
  workbook.created = new Date();

  // Hoja de instrucciones
  const instSheet = workbook.addWorksheet('Instrucciones', { properties: { tabColor: { argb: 'FF332E5A' } } });
  instSheet.columns = [{ width: 80 }];
  instSheet.addRow(['📋 INSTRUCCIONES - EDICIÓN MASIVA DE PRODUCTOS']);
  instSheet.addRow([]);
  instSheet.addRow(['1. Las columnas con 🔒 están BLOQUEADAS - No las modifiques (son llaves para identificar registros)']);
  instSheet.addRow(['2. Solo edita las columnas: nombre, descripcion, categoria']);
  instSheet.addRow(['3. Para "categoria", usa exactamente los nombres de la hoja "Categorías (Referencia)"']);
  instSheet.addRow(['4. Guarda el archivo y súbelo en la sección "Editar con Excel"']);
  instSheet.addRow([]);
  instSheet.addRow(['⚠️ IMPORTANTE: No elimines filas ni cambies los valores de id_product']);
  instSheet.getRow(1).font = { bold: true, size: 14 };
  instSheet.getRow(1).fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'FF332E5A' }
  };
  instSheet.getRow(1).font = { bold: true, size: 14, color: { argb: 'FFFFFFFF' } };

  // Hoja de categorías de referencia
  const catSheet = workbook.addWorksheet('Categorías (Referencia)', { properties: { tabColor: { argb: 'FF28a745' } } });
  catSheet.columns = [
    { width: 15, header: 'id_category' },
    { width: 25, header: 'tipo_categoria' },
    { width: 30, header: 'categoria' }
  ];
  const catHeaderRow = catSheet.getRow(1);
  catHeaderRow.font = { bold: true };
  catHeaderRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF28a745' } };
  catHeaderRow.font = { bold: true, color: { argb: 'FFFFFFFF' } };
  (categories || []).forEach((c) => {
    catSheet.addRow([c.id_category, c.typeName || c.categoryTypeName || '-', c.category_name || c.categoryName]);
  });

  // Hoja de productos a editar
  const prodSheet = workbook.addWorksheet('Productos', { properties: { tabColor: { argb: 'FFEA9D0E' } } });
  prodSheet.columns = [
    { width: 12, header: 'id_product 🔒' },
    { width: 35, header: 'nombre' },
    { width: 50, header: 'descripcion' },
    { width: 25, header: 'categoria' }
  ];

  const prodHeaderRow = prodSheet.getRow(1);
  prodHeaderRow.font = { bold: true };
  prodHeaderRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFEA9D0E' } };
  prodHeaderRow.font = { bold: true, color: { argb: 'FF332E5A' } };

  (products || []).forEach((p) => {
    prodSheet.addRow([
      p.id_product,
      p.name || '',
      p.description || '',
      p.category_name || ''
    ]);
  });

  // Protección: desbloquear celdas editables (columnas B, C, D), bloquear A (id_product)
  prodSheet.eachRow((row, rowNumber) => {
    row.eachCell((cell, colNumber) => {
      // Columna 1 = id_product (bloqueada)
      cell.protection = colNumber === 1 ? { locked: true } : { locked: false };
    });
  });
  prodSheet.protect('', {
    selectLockedCells: true,
    selectUnlockedCells: true
  });

  return workbook;
};

/**
 * Crea una plantilla Excel para editar items
 * Columnas bloqueadas: id_item, id_product, producto
 * Columnas editables: descripcion, sku, precio, stock, color, talla, gramaje
 */
export const generateBulkEditItemsExcel = async (items, colors, sizes, weights) => {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'DotoniWoof';
  workbook.created = new Date();

  // Hoja de instrucciones
  const instSheet = workbook.addWorksheet('Instrucciones', { properties: { tabColor: { argb: 'FF332E5A' } } });
  instSheet.columns = [{ width: 80 }];
  instSheet.addRow(['📋 INSTRUCCIONES - EDICIÓN MASIVA DE ITEMS']);
  instSheet.addRow([]);
  instSheet.addRow(['1. Las columnas con 🔒 están BLOQUEADAS - No las modifiques']);
  instSheet.addRow(['2. Edita: descripcion, sku, precio, stock, color, talla, gramaje']);
  instSheet.addRow(['3. Para color/talla/gramaje, usa los nombres de las hojas de referencia']);
  instSheet.addRow(['4. Usa "N/A" o vacío si no aplica']);
  instSheet.addRow([]);
  instSheet.addRow(['⚠️ IMPORTANTE: No elimines filas ni modifiques id_item, id_product o producto']);
  instSheet.getRow(1).font = { bold: true, size: 14 };
  instSheet.getRow(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF332E5A' } };
  instSheet.getRow(1).font = { bold: true, size: 14, color: { argb: 'FFFFFFFF' } };

  // Hoja de colores de referencia
  const colSheet = workbook.addWorksheet('Colores (Referencia)', { properties: { tabColor: { argb: 'FF17a2b8' } } });
  colSheet.columns = [{ width: 12, header: 'id_color' }, { width: 25, header: 'color' }];
  colSheet.getRow(1).font = { bold: true };
  colSheet.getRow(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF17a2b8' } };
  colSheet.getRow(1).font = { bold: true, color: { argb: 'FFFFFFFF' } };
  (colors || []).forEach((c) => colSheet.addRow([c.id_color, c.color_name]));
  colSheet.addRow([null, 'N/A']);

  // Hoja de tallas de referencia
  const sizeSheet = workbook.addWorksheet('Tallas (Referencia)', { properties: { tabColor: { argb: 'FF6f42c1' } } });
  sizeSheet.columns = [{ width: 12, header: 'id_size' }, { width: 25, header: 'talla' }];
  sizeSheet.getRow(1).font = { bold: true };
  sizeSheet.getRow(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF6f42c1' } };
  sizeSheet.getRow(1).font = { bold: true, color: { argb: 'FFFFFFFF' } };
  (sizes || []).forEach((s) => sizeSheet.addRow([s.id_size, s.size_name]));
  sizeSheet.addRow([null, 'N/A']);

  // Hoja de gramajes de referencia
  const weightSheet = workbook.addWorksheet('Gramajes (Referencia)', { properties: { tabColor: { argb: 'FFfd7e14' } } });
  weightSheet.columns = [{ width: 12, header: 'id_weight' }, { width: 25, header: 'gramaje' }];
  weightSheet.getRow(1).font = { bold: true };
  weightSheet.getRow(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFfd7e14' } };
  weightSheet.getRow(1).font = { bold: true, color: { argb: 'FFFFFFFF' } };
  (weights || []).forEach((w) => weightSheet.addRow([w.id_weight, w.weight_name]));
  weightSheet.addRow([null, 'N/A']);

  // Hoja de items a editar
  const itemSheet = workbook.addWorksheet('Items', { properties: { tabColor: { argb: 'FFEA9D0E' } } });
  itemSheet.columns = [
    { width: 10, header: 'id_item 🔒' },
    { width: 10, header: 'id_product 🔒' },
    { width: 25, header: 'producto 🔒' },
    { width: 40, header: 'descripcion' },
    { width: 15, header: 'sku' },
    { width: 12, header: 'precio' },
    { width: 10, header: 'stock' },
    { width: 15, header: 'color' },
    { width: 15, header: 'talla' },
    { width: 15, header: 'gramaje' }
  ];

  const itemHeaderRow = itemSheet.getRow(1);
  itemHeaderRow.font = { bold: true };
  itemHeaderRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFEA9D0E' } };
  itemHeaderRow.font = { bold: true, color: { argb: 'FF332E5A' } };

  (items || []).forEach((i) => {
    const colorName = i.color_name || i.color?.color_name || 'N/A';
    const sizeName = i.size_name || i.size?.size_name || 'N/A';
    const weightName = i.weight_name || i.weight?.weight_name || 'N/A';
    itemSheet.addRow([
      i.id_item,
      i.id_product,
      i.product_name || i.product?.name || '-',
      i.description || '',
      i.sku || '',
      i.price ?? 0,
      i.stock ?? 0,
      colorName,
      sizeName,
      weightName
    ]);
  });

  // Protección: bloquear columnas 1, 2, 3 (id_item, id_product, producto)
  itemSheet.eachRow((row, rowNumber) => {
    row.eachCell((cell, colNumber) => {
      cell.protection = colNumber <= 3 ? { locked: true } : { locked: false };
    });
  });
  itemSheet.protect('', {
    selectLockedCells: true,
    selectUnlockedCells: true
  });

  return workbook;
};

/**
 * Parsea archivo Excel de productos editados
 */
export const parseBulkEditProductsExcel = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const buffer = new Uint8Array(e.target.result);
        const workbook = new ExcelJS.Workbook();
        await workbook.xlsx.load(buffer);
        const sheet = workbook.getWorksheet('Productos');
        if (!sheet) {
          reject(new Error('No se encontró la hoja "Productos"'));
          return;
        }
        const rows = [];
        sheet.eachRow((row, rowNumber) => {
          if (rowNumber === 1) return; // skip header
          const values = row.values;
          if (values && values[1] != null && values[1] !== '') {
            rows.push({
              id_product: values[1],
              name: values[2] != null ? String(values[2]).trim() : '',
              description: values[3] != null ? String(values[3]).trim() : '',
              categoria: values[4] != null ? String(values[4]).trim() : ''
            });
          }
        });
        resolve(rows);
      } catch (err) {
        reject(err);
      }
    };
    reader.onerror = reject;
    reader.readAsArrayBuffer(file);
  });
};

/**
 * Parsea archivo Excel de items editados
 */
export const parseBulkEditItemsExcel = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const buffer = new Uint8Array(e.target.result);
        const workbook = new ExcelJS.Workbook();
        await workbook.xlsx.load(buffer);
        const sheet = workbook.getWorksheet('Items');
        if (!sheet) {
          reject(new Error('No se encontró la hoja "Items"'));
          return;
        }
        const rows = [];
        sheet.eachRow((row, rowNumber) => {
          if (rowNumber === 1) return;
          const values = row.values;
          if (values && values[1] != null && values[1] !== '') {
            rows.push({
              id_item: values[1],
              id_product: values[2],
              producto: values[3],
              description: values[4] != null ? String(values[4]).trim() : '',
              sku: values[5] != null ? String(values[5]).trim() : '',
              precio: parseFloat(values[6]) || 0,
              stock: parseInt(values[7], 10) || 0,
              color: values[8] != null ? String(values[8]).trim() : '',
              talla: values[9] != null ? String(values[9]).trim() : '',
              gramaje: values[10] != null ? String(values[10]).trim() : ''
            });
          }
        });
        resolve(rows);
      } catch (err) {
        reject(err);
      }
    };
    reader.onerror = reject;
    reader.readAsArrayBuffer(file);
  });
};

/**
 * Descarga un workbook Excel
 */
export const downloadBulkEditExcel = async (workbook, filename) => {
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
};
